import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import MuiTable from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CircularProgress from '@material-ui/core/CircularProgress';
import TablePagination from '@material-ui/core/TablePagination';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import Toolbar from '@material-ui/core/Toolbar';
import Skeleton from '@material-ui/lab/Skeleton';
import Search from './Input/Search';
import useI18n from '../i18n/useI18n';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    overflowX: 'auto',
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  table: {
    minWidth: 500,
    maxWidth: '100%',
    tableLayout: 'fixed',
  },
  tableHead: {
    background: theme.palette.grey[100],
    // borderTop: `1px solid ${theme.palette.grey['300']}`,
  },
  tableBody: {
    '& > *': {
      // verticalAlign: 'baseline',
      // wordBreak: 'break-all',
    },
    '& > :nth-child(even)': {
      // background: theme.palette.grey[50],
    },
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: theme.spacing(0, 0, 0, 2),
  },
  titleWithSpinner: {
    display: 'flex',
    alignItems: 'center',
  },
  search: {
    justifySelf: 'center',
  },
}));

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function tableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc'
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

const filterByQuery = (query, queryAttributes) => p => {
  if (!query || query === '' || query.length < 3) return true;
  return queryAttributes.some(attr => {
    if (typeof attr === 'function') {
      return attr(p, query.toLowerCase());
    } else {
      return p[attr] && p[attr].toLowerCase().includes(query.toLowerCase());
    }
  });
};

const useTabelCellSyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
  },
}));

const TableCell = props => {
  const classes = useTabelCellSyles();
  return <MuiTableCell classes={classes} {...props} />;
};

const Table = props => {
  const {
    headerAttributes,
    queryAttributes,
    // sortByLabel,
    renderCell,
    ActionCell,
    dataLabel,
    searchPlaceholder,
    actionCellWidth,
    defaultOrderBy,
    isLoading,
  } = props;
  const classes = useStyles();
  const [query, setQuery] = React.useState('');
  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [order, setOrder] = React.useState('desc');
  const [orderBy, setOrderBy] = React.useState(
    defaultOrderBy || headerAttributes[0].key,
  );
  const i18n = useI18n();
  const dLabel = dataLabel || i18n.t('table.dataLabel');
  const sPlaceholder = searchPlaceholder || i18n.t('table.searchPlaceholder');

  const handleChangeOrder = (e, property) => {
    let newOrder = 'desc';
    if (orderBy === property && order === 'desc') {
      newOrder = 'asc';
    }
    setOrderBy(property);
    setOrder(newOrder);
  };

  const handleChangePage = (event, index) => {
    setCurrentPageIndex(index);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(event.target.value);
  };

  const handleSetQuery = e => {
    setQuery(e.target.value);
  };

  const filteredData = props.data.filter(filterByQuery(query, queryAttributes));

  const sortedData = tableSort(filteredData, getSorting(order, orderBy)).slice(
    currentPageIndex * rowsPerPage,
    currentPageIndex * rowsPerPage + rowsPerPage,
  );

  return (
    <React.Fragment>
      <Toolbar disableGutters className={classes.toolbar}>
        <Typography variant="body1" className={classes.titleWithSpinner}>
          {filteredData.length} {dLabel}{' '}
          {query !== '' && `${i18n.t('table.withSearch')} '${query}'`}{' '}
          {i18n.t('table.found')}
          {isLoading && (
            <Box ml={1}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Typography>
        <Search
          value={query}
          onChange={handleSetQuery}
          className={classes.search}
          margin="none"
          placeholder={sPlaceholder}
        />
      </Toolbar>
      <Paper elevation={0} className={classes.root}>
        <MuiTable className={classes.table}>
          <TableHead className={classes.tableHead}>
            <TableRow>
              {headerAttributes.map(({ key, label, width }) => (
                <TableCell
                  key={key}
                  sortDirection={orderBy === key ? order : false}
                  style={{ width }}
                >
                  <Tooltip
                    title={i18n.t('table.sortByLabel', { label })}
                    placement="bottom"
                  >
                    <TableSortLabel
                      active={orderBy === key}
                      direction={order}
                      onClick={e => handleChangeOrder(e, key)}
                    >
                      {label}
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              ))}
              {ActionCell && <TableCell style={{ width: actionCellWidth }} />}
            </TableRow>
          </TableHead>
          <TableBody className={classes.tableBody}>
            {props.data.length === 0 &&
              Array.from(Array(3).keys()).map(() => (
                <TableRow>
                  {headerAttributes.map(({ key, label, width }) => (
                    <TableCell
                      key={key}
                      sortDirection={orderBy === key ? order : false}
                      style={{ width }}
                    >
                      <Skeleton height={10} width="75%" />
                    </TableCell>
                  ))}
                  {ActionCell && (
                    <TableCell
                      style={{ width: actionCellWidth, textAlign: 'right' }}
                    >
                      <Skeleton
                        component="div"
                        variant="circle"
                        width={40}
                        height={40}
                        style={{ display: 'inline-block' }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {sortedData.map(datum => (
              <TableRow className={classes.row} key={datum.uuid}>
                {headerAttributes.map(({ key }) => (
                  <TableCell key={key}>{renderCell(datum, key)}</TableCell>
                ))}
                {ActionCell && <ActionCell datum={datum} />}
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </Paper>
      <TablePagination
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={currentPageIndex}
        backIconButtonProps={{
          'aria-label': 'Previous Page',
        }}
        nextIconButtonProps={{
          'aria-label': 'Next Page',
        }}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        backIconButtonText={i18n.t('table.backIconButtonText')}
        nextIconButtonText={i18n.t('table.nextIconButtonText')}
        labelRowsPerPage={i18n.t('table.labelRowsPerPage')}
        labelDisplayedRows={data => i18n.t('table.labelDisplayedRows', data)}
      />
    </React.Fragment>
  );
};

Table.propTypes = {
  data: PropTypes.array.isRequired,
  defaultOrderBy: PropTypes.string,
  dataLabel: PropTypes.string,
  queryAttributes: PropTypes.array,
  searchPlaceholder: PropTypes.string,
  headerAttributes: PropTypes.array.isRequired,
  // sortByLabel: PropTypes.func,
  renderCell: PropTypes.func,
  ActionCell: PropTypes.element,
  actionCellWidth: PropTypes.number,
  isLoading: PropTypes.bool,
};

Table.defaultProps = {
  isLoading: false,
  dataLabel: null,
  queryAttributes: ['title'],
  searchPlaceholder: null,
  headerAttributes: [
    {
      key: 'type',
      label: 'Typ',
      width: 110,
    },
  ],
  // sortByLabel: label => `Nach ${label} sortieren`,
  renderCell: (datum, key) => {
    return <Typography variant="body2">{datum[key]}</Typography>;
  },
  ActionCell: null,
  actionCellWidth: 150,
};

export default observer(Table);
